/**
 * @file schemas/laboratories.ts
 * @description 實驗室字典 Zod schemas（送驗實驗室管理頁與採樣下拉共用）。
 *
 * 與 client-lookups 不同，本字典含多個業務欄位（認證號 / 聯絡資訊 / 備註），
 * 故獨立成一組 schema，不複用 lookupBaseShape。
 */
import { z } from 'zod';
/**
 * 共用欄位 shape：除 name 外的所有可選/可選中項。
 *
 * 規則：
 *   - accreditationNo / phone / address / contactPerson 皆為可選字串，前端傳空字串會被 API 落為 null
 *   - remark 為長文字（DB text 無長度限制，但前端建議 < 5000）
 *   - isActive / sortOrder 帶預設值，建立時不傳即為 true / 0
 */
const baseShape = {
    accreditationNo: z.string().max(100).optional().nullable(),
    phone: z.string().max(50).optional().nullable(),
    address: z.string().max(500).optional().nullable(),
    contactPerson: z.string().max(100).optional().nullable(),
    remark: z.string().max(5000).optional().nullable(),
    /** 實驗室證書有效期（序119：到期前 30 天提醒管理組；YYYY-MM-DD） */
    certValidUntil: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional()
        .nullable(),
    isActive: z.boolean().default(true),
    sortOrder: z.number().int().min(0).default(0),
};
/** 建立實驗室（POST 校驗）；name 必填且全域 UNIQUE。 */
export const createLaboratorySchema = z.object({
    name: z.string().min(1).max(200),
    ...baseShape,
});
/** 局部更新（PATCH 校驗）；任何欄位皆可省略。 */
export const updateLaboratorySchema = createLaboratorySchema.partial();
/**
 * 列表 query 參數。
 *  - activeOnly=true 僅回啟用且未軟刪（給下拉用）
 *  - includeDeleted=true 回傳含已軟刪（admin 視圖；預設 false）
 */
export const laboratoryQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(100),
    search: z.string().optional(),
    activeOnly: z.coerce.boolean().optional(),
    includeDeleted: z.coerce.boolean().optional(),
});
/**
 * 出參 DTO。`inUseCount` 僅在管理列表模式下提供（給「已被 N 次引用」徽章），
 * 其它場景（單筆查詢、下拉）可省略。
 */
export const laboratoryDtoSchema = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    accreditationNo: z.string().nullable(),
    phone: z.string().nullable(),
    address: z.string().nullable(),
    contactPerson: z.string().nullable(),
    remark: z.string().nullable(),
    certValidUntil: z.string().nullable(),
    isActive: z.boolean(),
    sortOrder: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
    deletedAt: z.string().nullable(),
    inUseCount: z.number().int().optional(),
});
